import { Injectable, inject } from '@angular/core';
import { Borders, Workbook, Worksheet } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { RowInput, Styles } from 'jspdf-autotable';
import { reportConstants } from '../../common/constants';
import { reportConstantsMessage } from '../../common/constants';
import { CommonBodyStyles, CommonHeadStyles, CommonStyles, SeparatorOilProcessModel, SeparatorOilReports, SepOilPlusFractionResult } from '../../models/project';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { TableCell } from '../../models/lab-test';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFSeparatorOilRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    commonStyles: CommonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    commonHeadStyles: CommonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStyles: CommonBodyStyles = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    commonStylesWithoutBorder: CommonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    commonHeadStylesWithoutBorder: CommonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStylesWithoutBorder: CommonBodyStyles = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    async GenerateSeparatorOilExcelReport(workbook: Workbook, worksheetName: string, separatorData: SeparatorOilProcessModel, wellName: string, sampleName: string, subHeader: string) {
        try {
            const compAnalysisWorksheet = workbook.addWorksheet(worksheetName);
            const firstBlock = separatorData?.sheetOne.rows;
            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            compAnalysisWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(compAnalysisWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 65,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            compAnalysisWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(compAnalysisWorksheet, subHeader, reportConstants.c1, reportConstants.k1);
            compAnalysisWorksheet.getColumn(reportConstants.c).width = 12;
            compAnalysisWorksheet.getColumn(reportConstants.d).width = 12;
            compAnalysisWorksheet.getColumn(reportConstants.e).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.fChar).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.gColumn).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.h).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.i).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.j).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.k).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.l).width = 11;

            compAnalysisWorksheet.getColumn(reportConstants.a).width = 3;
            compAnalysisWorksheet.getColumn(reportConstants.m).width = 3;
            compAnalysisWorksheet.mergeCells(reportConstants.b4, reportConstants.h4);
            compAnalysisWorksheet.getRow(4).height = 20;
            const textReportTitleCell = compAnalysisWorksheet.getCell(reportConstants.b4);
            textReportTitleCell.value = wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            compAnalysisWorksheet.mergeCells(reportConstants.b5, reportConstants.e5);
            const textReportSubtitleCell = compAnalysisWorksheet.getCell(reportConstants.b5);
            textReportSubtitleCell.value = reportConstants.compositionData;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            const tableStartRow = 7
            compAnalysisWorksheet.getRow(tableStartRow).height = 20;
            compAnalysisWorksheet.getRow(tableStartRow + 1).height = 20;

            compAnalysisWorksheet.mergeCells(`E${tableStartRow}`, `F${tableStartRow}`);
            const textFlashedLiquidCell = compAnalysisWorksheet.getCell(`E${tableStartRow}`);
            textFlashedLiquidCell.value = reportConstants.flashedLiquid;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            const headerCellH = compAnalysisWorksheet.getCell(`G${tableStartRow}`);
            headerCellH.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`H${tableStartRow}`, `I${tableStartRow}`);
            const textFlashedGasCell = compAnalysisWorksheet.getCell(`H${tableStartRow}`);
            textFlashedGasCell.value = reportConstants.flashedGas;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            const headerCellK = compAnalysisWorksheet.getCell(`J${tableStartRow}`);
            headerCellK.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`K${tableStartRow}`, `L${tableStartRow}`);
            const textWholeFluidCell = compAnalysisWorksheet.getCell(`K${tableStartRow}`);
            textWholeFluidCell.value = reportConstants.wholeFluid;
            textWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textWholeFluidCell.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`B${tableStartRow + 1}`, `D${tableStartRow + 1}`);
            const textComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 1}`);
            textComponentCell.value = reportConstants.component;
            textComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textComponentCell.border = borderStyle;

            const textLiquidWeightPercentCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 1}`);
            textLiquidWeightPercentCell.value = reportConstants.weight;
            textLiquidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidWeightPercentCell.border = borderStyle;

            const textLiquidMolePercentCell = compAnalysisWorksheet.getCell(`F${tableStartRow + 1}`);
            textLiquidMolePercentCell.value = reportConstants.molePercent;
            textLiquidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidMolePercentCell.border = borderStyle;

            const subHeaderCellH = compAnalysisWorksheet.getCell(`G${tableStartRow + 1}`);
            subHeaderCellH.border = borderStyle;

            const textGasWeightPercentCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 1}`);
            textGasWeightPercentCell.value = reportConstants.weight;
            textGasWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasWeightPercentCell.border = borderStyle;

            const textGasMolePercentCell = compAnalysisWorksheet.getCell(`I${tableStartRow + 1}`);
            textGasMolePercentCell.value = reportConstants.molePercent;
            textGasMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasMolePercentCell.border = borderStyle;

            const subHeaderCellK = compAnalysisWorksheet.getCell(`J${tableStartRow + 1}`);
            subHeaderCellK.border = borderStyle;

            const textFluidWeightPercentCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 1}`);
            textFluidWeightPercentCell.value = reportConstants.weight;
            textFluidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidWeightPercentCell.border = borderStyle;

            const textFluidMolePercentCell = compAnalysisWorksheet.getCell(`L${tableStartRow + 1}`);
            textFluidMolePercentCell.value = reportConstants.molePercent;
            textFluidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidMolePercentCell.border = borderStyle;

            const formatedData = this._reportGeneralUtilityService.FormatTableDataComposition(firstBlock);

            for (let i = 0; i < formatedData.length; i++) {
                const row = formatedData[i];
                const component = row[0];
                let minusCount = 0;
                const isObject = typeof component === 'object' && component !== null && 'rowSpan' in component && 'content' in component;

                if (isObject && component.rowSpan > 1) {
                    compAnalysisWorksheet.mergeCells(`B${tableStartRow + 2 + i}:B${tableStartRow + 2 + i + component.rowSpan - 1}`);
                }

                if (isObject) {
                    const textComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 2 + i}`);
                    textComponentCell.value = component.content;
                    textComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                    textComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    textComponentCell.border = borderStyle;
                }
                else {
                    minusCount = 1;
                }

                compAnalysisWorksheet.mergeCells(`C${tableStartRow + 2 + i}`, `D${tableStartRow + 2 + i}`);
                const textComponentNameCell = compAnalysisWorksheet.getCell(`C${tableStartRow + 2 + i}`);
                textComponentNameCell.value = this.GetCellValue(row[1 - minusCount]);
                textComponentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                textComponentNameCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textComponentNameCell.border = borderStyle;

                const textLiquidWeightPercentCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textLiquidWeightPercentCell, row[2 - minusCount]);
                textLiquidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textLiquidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textLiquidWeightPercentCell.border = borderStyle;

                const textLiquidMolePercentCell = compAnalysisWorksheet.getCell(`F${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textLiquidMolePercentCell, row[3 - minusCount]);
                textLiquidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textLiquidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textLiquidMolePercentCell.border = borderStyle;

                const textCellH = compAnalysisWorksheet.getCell(`G${tableStartRow + 2 + i}`);
                textCellH.border = borderStyle;

                const textGasWeightPercentCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textGasWeightPercentCell, row[5 - minusCount]);
                textGasWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textGasWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textGasWeightPercentCell.border = borderStyle;

                const textGasMolePercentCell2 = compAnalysisWorksheet.getCell(`I${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textGasMolePercentCell2, row[6 - minusCount]);
                textGasMolePercentCell2.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textGasMolePercentCell2.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textGasMolePercentCell2.border = borderStyle;

                const textCellK = compAnalysisWorksheet.getCell(`J${tableStartRow + 2 + i}`);
                textCellK.border = borderStyle;

                const textFluidWeightPercentCell2 = compAnalysisWorksheet.getCell(`K${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textFluidWeightPercentCell2, row[8 - minusCount]);
                textFluidWeightPercentCell2.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textFluidWeightPercentCell2.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textFluidWeightPercentCell2.border = borderStyle;

                const textFluidMolePercentCell2 = compAnalysisWorksheet.getCell(`L${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textFluidMolePercentCell2, row[9 - minusCount]);
                textFluidMolePercentCell2.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textFluidMolePercentCell2.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textFluidMolePercentCell2.border = borderStyle;
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateSeparatorOilFlashGORData(workbook: Workbook, worksheetName: string, separatorData: SeparatorOilProcessModel, wellName: string, sampleName: string, subHeader: string) {
        try {

            const singleStageFlashGORWorksheet = workbook.addWorksheet(worksheetName);
            const firstBlock = separatorData?.sheetTwo;

            const plusFracArray = firstBlock.plusFractionProperties;
            const wholeFluidPropertyArray = firstBlock.flashedSampleProperties;
            const compFlashGOR = firstBlock.compositionalFlash;
            const volFlashGOR = firstBlock.volumetricFlashResult;
            const volumetricFlashPressure = firstBlock.volumetricFlashPressure;
            const volumetricFlashTemperature = firstBlock.volumetricFlashTemperature;
            const compositionalFlashPressure = firstBlock.compositionalFlashPressure;
            const compositionalFlashTemperature = firstBlock.compositionalFlashTemperature;

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            singleStageFlashGORWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(singleStageFlashGORWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 65,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
;
            singleStageFlashGORWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            singleStageFlashGORWorksheet.getColumn(reportConstants.fChar).width = 15;
            singleStageFlashGORWorksheet.getColumn(reportConstants.gColumn).width = 15;
            singleStageFlashGORWorksheet.getColumn(reportConstants.h).width = 15;

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(singleStageFlashGORWorksheet, subHeader, reportConstants.c1, reportConstants.j1);

            singleStageFlashGORWorksheet.getRow(3).height = 20;
            singleStageFlashGORWorksheet.getRow(4).height = 20;
            singleStageFlashGORWorksheet.getRow(5).height = 20;

            singleStageFlashGORWorksheet.mergeCells(reportConstants.a3, reportConstants.h3);
            const textReportTitleCell = singleStageFlashGORWorksheet.getCell(reportConstants.a3);
            textReportTitleCell.value = wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.mergeCells(reportConstants.a4, reportConstants.e4);
            const textReportSubtitleCell = singleStageFlashGORWorksheet.getCell(reportConstants.a4);
            textReportSubtitleCell.value = reportConstants.singleStageFlashGORData;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b5, reportConstants.e5);
            const textCompositionalFlashCell = singleStageFlashGORWorksheet.getCell(reportConstants.b5);
            textCompositionalFlashCell.value = reportConstants.compositionalFlash;
            textCompositionalFlashCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textCompositionalFlashCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b6, reportConstants.g6);
            const textFlashConditionsCompCell = singleStageFlashGORWorksheet.getCell(reportConstants.b6);
            textFlashConditionsCompCell.value = reportConstants.flashConditions;
            textFlashConditionsCompCell.value = `${reportConstants.flashConditionsExtra}: ${compositionalFlashPressure} ${reportConstants.psiaAnd} ${compositionalFlashTemperature} ${reportConstants.fTemp}`;
            textFlashConditionsCompCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashConditionsCompCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.bottom };

            let rowNo = 7;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.gasOilRatio, (compFlashGOR?.gorscfstb ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.formationVolFactor, (compFlashGOR?.formVolFactor ?? '').toString(), rowNo, borderStyle);
            rowNo += 2;

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textCalcPlusFractionCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textCalcPlusFractionCell.value = reportConstants.calculatedPlusFractionProperties;
            textCalcPlusFractionCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textCalcPlusFractionCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo += 2;

            plusFracArray.forEach((plusFracObj: SepOilPlusFractionResult) => {
                PopulateCalcPlusFractionData(singleStageFlashGORWorksheet, plusFracObj, rowNo, borderStyle);
                rowNo += 5;
            });

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textFlashedSamplePropertiesCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textFlashedSamplePropertiesCell.value = reportConstants.flashedSampleProperties;
            textFlashedSamplePropertiesCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedSamplePropertiesCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            PopulateFlashedFluidHeaders(singleStageFlashGORWorksheet, '', rowNo, borderStyle);
            rowNo++;

            const oilAPIGravity = wholeFluidPropertyArray.measuredDensityFlashedLiquid;
            const oilAPIGravityComp = ((reportConstants.oilFlashValue1 / (oilAPIGravity ?? 0 / reportConstants.oilFlashValue2)) - reportConstants.oilFlashValue3).toFixed(1);

            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.calculatedAvgMW, (wholeFluidPropertyArray?.calculatedAvgMWFlashedLiquid ?? '').toString(), (wholeFluidPropertyArray?.calculatedAvgMWFlashedGas ?? '').toString(), (wholeFluidPropertyArray?.calculatedAvgMWWholeFluid ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.measuredAvgMW, (wholeFluidPropertyArray?.measuredAvgMWFlashedLiquid ?? '').toString(), '', '', rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.calculatedDensity, (wholeFluidPropertyArray?.calculatedDensityFlashedLiquid ?? '').toString(), '', '', rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.measuredDensity, (wholeFluidPropertyArray?.measuredDensityFlashedLiquid ?? '').toString(), '', '', rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.moleFraction, (wholeFluidPropertyArray?.moleFractionFlashedLiquid ?? '').toString(), (wholeFluidPropertyArray?.moleFractionFlashedGas ?? '').toString(), (wholeFluidPropertyArray?.moleFractionWholeFluid ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.massFraction, (wholeFluidPropertyArray?.massFractionFlashedLiquid ?? '').toString(), (wholeFluidPropertyArray?.massFractionFlashedGas ?? '').toString(), (wholeFluidPropertyArray?.massFractionWholeFluid ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.gasGravity, '', (wholeFluidPropertyArray?.gasGravityFlashedGas ?? '').toString(), '', rowNo, borderStyle);
            rowNo++;
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.oilAPIGravity, oilAPIGravityComp, '', '', rowNo);
            rowNo += 2;

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textVolumetricFlashCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textVolumetricFlashCell.value = reportConstants.volumetricFlash;
            textVolumetricFlashCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textVolumetricFlashCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo += 2;

            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + (rowNo + 1));
            const textFlashConditionsVolumeCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textFlashConditionsVolumeCell.value = `${reportConstants.flashConditionsExtra}: ${volumetricFlashPressure} ${reportConstants.psiaAnd} ${volumetricFlashTemperature} ${reportConstants.fTemp}`;
            textFlashConditionsVolumeCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashConditionsVolumeCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.bottom };
            rowNo += 2;

            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.gasOilRatio, (volFlashGOR?.gorscfstb ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.formationVolFactor, (volFlashGOR?.formVolFactor ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.measuredDensityWithoutF, (volFlashGOR?.measuredDensity ?? '').toString(), rowNo, borderStyle);
            rowNo++;
            PopulateFlashConditions(singleStageFlashGORWorksheet, reportConstants.oilAPIGravity, (volFlashGOR?.oilAPIGravity ?? '').toString(), rowNo);
            rowNo += 2;

            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.specificGravity);
            rowNo++;
            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.gasGravityAir);
            rowNo++;
            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.gorMeasurementCorrected);
            rowNo++;
            PopulateComment(singleStageFlashGORWorksheet, rowNo, reportConstants.formationVolumeFactor);
            rowNo += 2;

            AddOuterBorder(singleStageFlashGORWorksheet, 3, rowNo, 1, 10);

        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }


        function PopulateComment(singleStageFlashGORWorksheet: Worksheet, rowNo: number, comments: string) {
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.h + rowNo);
            const textCommentsCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textCommentsCell.value = comments;
            textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: 9, bold: false };
            textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        }

        function PopulateFlashConditions(
            singleStageFlashGORWorksheet: Worksheet,
            label: string,
            value: string,
            rowNo: number,
            borderStyle?: Partial<Borders>
        ) {
            singleStageFlashGORWorksheet.getRow(rowNo).height = 25;
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.e + rowNo);
            const textGasOilRatioCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + rowNo);
            textGasOilRatioCell.value = label;
            textGasOilRatioCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textGasOilRatioCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) {
                textGasOilRatioCell.border = borderStyle;
            }


            singleStageFlashGORWorksheet.mergeCells(reportConstants.f + rowNo, reportConstants.gColumn + rowNo);
            const valueGasOilRatioCell = singleStageFlashGORWorksheet.getCell(reportConstants.f + rowNo);
            valueGasOilRatioCell.value = value;
            valueGasOilRatioCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            valueGasOilRatioCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) {
                valueGasOilRatioCell.border = borderStyle;
            }
        }

        function PopulateCalcPlusFractionData(singleStageFlashGORWorksheet: Worksheet, plusFracObj: SepOilPlusFractionResult, tableStartRow: number, borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; }; }) {

            PopulateFlashedFluidHeaders(singleStageFlashGORWorksheet, plusFracObj.compound, tableStartRow, borderStyle as Partial<Borders>);

            const molePercentFlashedGas = plusFracObj.compound.toLowerCase() === reportConstants.compc7Plus ? plusFracObj.flashedGasMolePercentage : '-';
            const molWeightFlashedGas = plusFracObj.compound.toLowerCase() === reportConstants.compc7Plus ? plusFracObj.flashedGasMolWeight : '-';
            const specificGravityFlashedGas = plusFracObj.compound.toLowerCase() === reportConstants.compc7Plus ? plusFracObj.flashedGasSpecificGravity : '-';

            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.molePercent, (plusFracObj?.flashedLiquidMolePercentage ?? '').toString(), (molePercentFlashedGas ?? '').toString(), (plusFracObj.wholeFluidMolePercentage ?? '').toString(), (tableStartRow + 1), borderStyle as Partial<Borders>);
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.moleWeight, (plusFracObj?.flashedLiquidMolWeight ?? '').toString(), (molWeightFlashedGas ?? '').toString(), (plusFracObj.wholeFluidMolWeight ?? '').toString(), (tableStartRow + 2), borderStyle as Partial<Borders>);
            PopulateFlashedFluidValues(singleStageFlashGORWorksheet, reportConstants.specificGravitySubString, (plusFracObj.flashedLiquidSpecificGravity ?? '').toString(), (specificGravityFlashedGas ?? '').toString(), (plusFracObj.wholeFluidSpecificGravity ?? '').toString(), (tableStartRow + 3), borderStyle as Partial<Borders>);

        }

        function PopulateFlashedFluidHeaders(singleStageFlashGORWorksheet: Worksheet, compound: string, tableStartRow: number, borderStyle: Partial<Borders>) {
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + tableStartRow, reportConstants.e + tableStartRow);
            const textC36PlusCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + tableStartRow);
            textC36PlusCell.value = compound;
            textC36PlusCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textC36PlusCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            const textFlashedLiquidCell = singleStageFlashGORWorksheet.getCell(reportConstants.f + tableStartRow);
            textFlashedLiquidCell.value = reportConstants.flashedLiquid;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            const textFlashedGasCell = singleStageFlashGORWorksheet.getCell(reportConstants.gColumn + tableStartRow);
            textFlashedGasCell.value = reportConstants.flashedGas;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            const textWholeFluidCell = singleStageFlashGORWorksheet.getCell(reportConstants.h + tableStartRow);
            textWholeFluidCell.value = reportConstants.wholeFluid;
            textWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textWholeFluidCell.border = borderStyle;
        }

        function PopulateFlashedFluidValues(
            singleStageFlashGORWorksheet: Worksheet,
            content: string,
            flashedLiquid: string,
            flashedGas: string,
            wholeFluid: string,
            tableStartRow: number,
            borderStyle?: Partial<Borders>
        ) {
            singleStageFlashGORWorksheet.mergeCells(reportConstants.b + tableStartRow, reportConstants.e + tableStartRow);
            const textMolePercentCell = singleStageFlashGORWorksheet.getCell(reportConstants.b + tableStartRow);
            textMolePercentCell.value = content;
            textMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            textMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) {
                textMolePercentCell.border = borderStyle;
            }

            const molePercentFlashedLiquidCell = singleStageFlashGORWorksheet.getCell(reportConstants.f + tableStartRow);
            molePercentFlashedLiquidCell.value = flashedLiquid;
            molePercentFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            molePercentFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) {
                molePercentFlashedLiquidCell.border = borderStyle;
            }

            const molePercentFlashedGasCell = singleStageFlashGORWorksheet.getCell(reportConstants.gColumn + tableStartRow);
            molePercentFlashedGasCell.value = flashedGas;
            molePercentFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            molePercentFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) {
                molePercentFlashedGasCell.border = borderStyle;
            }

            const molePercentWholeFluidCell = singleStageFlashGORWorksheet.getCell(reportConstants.h + tableStartRow);
            molePercentWholeFluidCell.value = wholeFluid;
            molePercentWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            molePercentWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            if (borderStyle) {
                molePercentWholeFluidCell.border = borderStyle;
            }
        }

        function AddOuterBorder(
            worksheet: Worksheet,
            startRowNo: number,
            endRowNo: number,
            startColumnNo: number,
            endColumnNo: number
        ) {
            for (let r = startRowNo; r <= endRowNo; r++) {
                for (let c = startColumnNo; c <= endColumnNo; c++) {
                    const cell = worksheet.getCell(r, c).master;
                    const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                    const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                    const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                    const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                    cell.border = {
                        top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                        bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                        left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                        right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                    };
                }
            }
        }
    }

    async GenerateSeparatorOilMaterialBalanceHoffmanPlot(workbook: Workbook, worksheetName: string, graphImages: string[], wellName: string, sampleName: string, subHeader: string) {
        try {
            const graphsWorksheet = workbook.addWorksheet(worksheetName);

            graphsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(graphsWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 65,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            graphsWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(graphsWorksheet, subHeader, reportConstants.d1, reportConstants.l1);

            graphsWorksheet.getRow(4).height = 25;
            graphsWorksheet.getRow(5).height = 25;
            graphsWorksheet.getColumn(12).width = 15;
            graphsWorksheet.mergeCells(reportConstants.a4, reportConstants.h4);
            const textReportTitleCell = graphsWorksheet.getCell(reportConstants.a4);
            textReportTitleCell.value = wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            graphsWorksheet.mergeCells(reportConstants.a5, reportConstants.h5);
            const textReportSubtitleCell = graphsWorksheet.getCell(reportConstants.a5);
            textReportSubtitleCell.value = reportConstants.materialBalanceCheckAndHoffmanPlot;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };


            const materialBalanceId = workbook.addImage({
                base64: graphImages[0],
                extension: reportConstants.png,
            });

            graphsWorksheet.mergeCells(reportConstants.c9, reportConstants.l34);
            graphsWorksheet.addImage(materialBalanceId, {
                tl: { col: 1, row: 6 },
                ext: { width: 700, height: 500 }
            });


            const hoffmanPlotId = workbook.addImage({
                base64: graphImages[1],
                extension: reportConstants.png,
            });

            graphsWorksheet.mergeCells(reportConstants.c35, reportConstants.l50);
            graphsWorksheet.addImage(hoffmanPlotId, {
                tl: { col: 1, row: 35 },
                ext: { width: 700, height: 500 }
            });

            const rowNo = 70;
            AddOuterBorder(graphsWorksheet, 3, rowNo, 1, 12);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function AddOuterBorder(
            worksheet: Worksheet,
            startRowNo: number,
            endRowNo: number,
            startColumnNo: number,
            endColumnNo: number
        ) {
            for (let r = startRowNo; r <= endRowNo; r++) {
                for (let c = startColumnNo; c <= endColumnNo; c++) {
                    const cell = worksheet.getCell(r, c).master;
                    const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                    const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                    const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                    const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                    cell.border = {
                        top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                        bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                        left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                        right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                    };
                }
            }
        }
    }

    GenerateSeparatorOilPdfReport(doc: jsPDF, marginSize: number, separatorData: SeparatorOilReports, wellName: string, sampleName: string) {
        if (separatorData !== undefined && separatorData !== null) {
            separatorData.data.forEach((block: SeparatorOilProcessModel) => {
                const sheetTwo = block.sheetTwo;
                const sheetOne = block.sheetOne;


                const plusFracArray = sheetTwo.plusFractionProperties;
                const compositionFlash = sheetTwo.compositionalFlash;
                const compositionalFlashPressure = sheetTwo.compositionalFlashPressure;
                const flashedSampleProperties = sheetTwo.flashedSampleProperties;
                const volumetricFlashResult = sheetTwo.volumetricFlashResult
                const volumetricFlashPressure = sheetTwo.volumetricFlashPressure;
                const reportCompositionData = sheetOne.rows;

                marginSize = 30;
                const formatedData = this._reportGeneralUtilityService.FormatTableDataComposition(reportCompositionData);
                doc.addPage();
                doc.setFontSize(12);
                doc.setFont(reportConstants.eMprintCondensedBold);
                let currentY = 30;
                doc.text(wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis, marginSize, currentY);
                currentY += 5;
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.compositionData, marginSize, currentY);
                currentY += 5;
                let tableStartY = currentY;
                autoTable(doc, {
                    head: [
                        [
                            {
                                content: '',
                                colSpan: 2,
                                styles: { valign: reportConstants.middle, halign: reportConstants.center }
                            },
                            { content: reportConstants.flashedLiquid, colSpan: 2, styles: { halign: reportConstants.center } },
                            { content: '' },
                            { content: reportConstants.flashedGas, colSpan: 2, styles: { halign: reportConstants.center } },
                            { content: '' },
                            { content: reportConstants.wholeFluid, colSpan: 2, styles: { halign: reportConstants.center } }
                        ],
                        [
                            {
                                content: reportConstants.component,
                                colSpan: 2,
                                styles: { valign: reportConstants.middle, halign: reportConstants.center }
                            },
                            { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                            { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                            { content: '' },
                            { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                            { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                            { content: '' },
                            { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                            { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                        ]
                    ],
                    body: formatedData as RowInput[],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        lineColor: [0, 0, 0],
                        lineWidth: 0
                    },
                    headStyles: {
                        fontSize: 8,
                        halign: reportConstants.center,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        fontStyle: reportConstants.normal,
                        font: reportConstants.eMprintCondensedBold,
                        cellPadding: { top: 0.5, bottom: 0.5 },
                        lineWidth: 0 // Remove border
                    },
                    bodyStyles: {
                        fontSize: 8,
                        halign: reportConstants.center,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        font: reportConstants.eMprintCondensedRegular,
                        cellPadding: { top: 0.5, bottom: 0.5 },
                        lineWidth: 0
                    },
                    columnStyles: {
                        4: { cellWidth: 1 },
                        7: { cellWidth: 1 }
                    },
                    didParseCell: function (data) {
                        if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                            data.cell.styles.font = reportConstants.eMprintCondensedBold;
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section } = data;
                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else if (section === reportConstants.head && row.index != 1) {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.rect(cell.x, cell.y, cell.width, cell.height);
                        }
                        else if (section === reportConstants.body) {
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            // âœ… LEFT border
                            doc.line(x, y, x, y + h);
                            // âœ… RIGHT border
                            doc.line(x + w, y, x + w, y + h);
                            // âœ… BOTTOM border (draw ONCE per row)
                            doc.line(x, y + h, x + w, y + h);
                        }

                        if (section === reportConstants.head && row.index === 1) {
                            doc.setDrawColor(0, 0, 0);
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            doc.line(x, y + h - 0.5, x + w, y + h - 0.5); // first bottom line
                            doc.line(x, y + h, x + w, y + h);             // second bottom line
                            doc.line(x, y, x, y + h);                     // left
                            doc.line(x + w, y, x + w, y + h);             // right
                            doc.line(x, y, x + w, y);                     // top
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

                /*Single Stage Flash/GOR Data*/
                doc.addPage();
                currentY = 30;

                doc.setFontSize(10);
                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.text(wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis, marginSize, currentY);
                currentY += 5;

                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.singleStageFlashGORData, marginSize, currentY);
                currentY += 10;

                tableStartY = currentY;
                doc.setFont(reportConstants.eMprintCondensedBold);
                marginSize += 10;

                doc.text(reportConstants.compositionalFlash, marginSize, tableStartY);
                tableStartY += 5;

                doc.setFontSize(8);
                doc.text(`${reportConstants.flashConditionsExtra}: ${compositionalFlashPressure} ${reportConstants.psia120}`, marginSize + 5, tableStartY);
                tableStartY += 3;
                doc.setFont(reportConstants.eMprintCondensedRegular);

                autoTable(doc, {
                    head: [],
                    body: [
                        ['', compositionFlash?.gorscfstb ?? ''],
                        ['', compositionFlash?.formVolFactor ?? '']
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: this.commonStyles as Partial<Styles>,
                    headStyles: this.commonHeadStyles as Partial<Styles>,
                    bodyStyles: this.commonBodyStyles as Partial<Styles>,
                    tableWidth: doc.internal.pageSize.getWidth() * 0.3,
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 }
                    },
                    didDrawCell: function (data) {
                        if (data.column.index === 0 && data.row.index === 0) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.gor;
                            const subscript = reportConstants.gorThree;
                            const unit = reportConstants.gorUnit;

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }

                        if (data.column.index === 0 && data.row.index === 1) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.formationVolFactorPDF;
                            const subscript = reportConstants.formationVolFactorSubscript;
                            const unit = reportConstants.formationVolFactorUnit;

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4;
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.text(reportConstants.calculatedPlusFractionProperties, marginSize, tableStartY);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                tableStartY += 5;
                autoTable(doc, {
                    /* C36+ is a predefined component name(label)*/
                    head: [[reportConstants.c36Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                    body: [
                        [reportConstants.molePercent, plusFracArray[0].flashedLiquidMolePercentage, reportConstants.dash, plusFracArray[0].wholeFluidMolePercentage],
                        [reportConstants.molWeight, plusFracArray[0].flashedLiquidMolWeight, reportConstants.dash, plusFracArray[0].wholeFluidMolWeight],
                        ['', plusFracArray[0].flashedLiquidSpecificGravity, reportConstants.dash, plusFracArray[0].wholeFluidSpecificGravity]
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: this.commonStylesWithoutBorder as Partial<Styles>,
                    headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                    bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 }
                    },
                    didParseCell: function (data) {
                        // âœ… Apply bold only to header cell C36+
                        if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                            data.cell.styles.font = reportConstants.eMprintCondensedBold;
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section, table } = data;
                        const lw = reportConstants.tableLinewidth;
                        const snap = (v: number) => Math.round(v) + lw / 2;
                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right

                        }
                        else if (section === reportConstants.body && row.index === 7) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);
                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;

                            // âœ… LEFT border â†’ draw for EVERY column (creates column separators)
                            doc.line(x, y, x, y + h);

                            // âœ… RIGHT border â†’ ONLY for last column
                            if (column.index === table.columns.length - 1) {
                                doc.line(x + w, y, x + w, y + h);
                            }

                            // âœ… BOTTOM border (row separator)
                            doc.line(x, y + h, x + w, y + h);

                            // âœ… TOP border (header row only)
                            if (section === reportConstants.head) {
                                doc.line(x, y, x + w, y);
                            }
                            if (section === 'body' && row.index === 0 && column.index === 0) {
                                doc.line(x, y, x + w, y);
                            }
                        }

                        if (data.column.index === 0 && data.row.index === 2) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.specificGravityPDF;
                            const subscript = reportConstants.specificGravitySubscript;
                            const unit = '';

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
                /*C20*/
                autoTable(doc, {
                    head: [[reportConstants.c20Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                    body: [
                        [reportConstants.molePercent, plusFracArray[1].flashedLiquidMolePercentage, reportConstants.dash, plusFracArray[1].wholeFluidMolePercentage],
                        [reportConstants.mwgMol, plusFracArray[1].flashedLiquidMolWeight, reportConstants.dash, plusFracArray[1].wholeFluidMolWeight],
                        ['', plusFracArray[1].flashedLiquidSpecificGravity, reportConstants.dash, plusFracArray[1].wholeFluidSpecificGravity]
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: this.commonStylesWithoutBorder as Partial<Styles>,
                    headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                    bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 }
                    },
                    didParseCell: function (data) {
                        // âœ… Apply bold only to header cell C36+
                        if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                            data.cell.styles.font = reportConstants.eMprintCondensedBold;
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section } = data;
                        const lw = reportConstants.tableLinewidth;
                        const snap = (v: number) => Math.round(v) + lw / 2;
                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right

                        }
                        else if (section === reportConstants.body && row.index === 7) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);
                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            // âœ… LEFT border
                            doc.line(x, y, x, y + h);
                            // âœ… RIGHT border
                            doc.line(x + w, y, x + w, y + h);
                            // âœ… BOTTOM border (draw ONCE per row)
                            doc.line(x, y + h, x + w, y + h);
                            if (section === 'head') {
                                doc.line(x, y, x + w, y);
                            }
                            if (section === 'body' && row.index === 0 && column.index === 0) {
                                doc.line(x, y, x + w, y);
                            }
                        }
                        if (data.column.index === 0 && data.row.index === 2) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.specificGravityPDF;
                            const subscript = reportConstants.specificGravitySubscript;
                            const unit = '';

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

                /*C11+*/
                autoTable(doc, {
                    head: [[reportConstants.c11Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                    body: [
                        [reportConstants.molePercent, plusFracArray[2].flashedLiquidMolePercentage, reportConstants.dash, plusFracArray[2].wholeFluidMolePercentage],
                        [reportConstants.mwgMol, plusFracArray[2].flashedLiquidMolWeight, reportConstants.dash, plusFracArray[2].wholeFluidMolWeight],
                        ['', plusFracArray[2].flashedLiquidSpecificGravity, reportConstants.dash, plusFracArray[2].wholeFluidSpecificGravity]
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: this.commonStylesWithoutBorder as Partial<Styles>,
                    headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                    bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 }
                    },
                    didParseCell: function (data) {
                        // âœ… Apply bold only to header cell C36+
                        if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                            data.cell.styles.font = reportConstants.eMprintCondensedBold;
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section } = data;
                        const lw = reportConstants.tableLinewidth;
                        const snap = (v: number) => Math.round(v) + lw / 2;
                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right

                        }
                        else if (section === reportConstants.body && row.index === 7) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);
                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            // âœ… LEFT border
                            doc.line(x, y, x, y + h);
                            // âœ… RIGHT border
                            doc.line(x + w, y, x + w, y + h);
                            // âœ… BOTTOM border (draw ONCE per row)
                            doc.line(x, y + h, x + w, y + h);
                            if (section === 'head') {
                                doc.line(x, y, x + w, y);
                            }
                            if (section === 'body' && row.index === 0 && column.index === 0) {
                                doc.line(x, y, x + w, y);
                            }
                        }

                        if (data.column.index === 0 && data.row.index === 2) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.specificGravityPDF;
                            const subscript = reportConstants.specificGravitySubscript;
                            const unit = '';

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
                /*C7+*/
                autoTable(doc, {
                    head: [[reportConstants.c7Plus, reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                    body: [
                        [reportConstants.molePercent, plusFracArray[3].flashedLiquidMolePercentage, plusFracArray[3].flashedGasMolePercentage, plusFracArray[3].wholeFluidMolePercentage],
                        [reportConstants.mwgMol, plusFracArray[3].flashedLiquidMolWeight, plusFracArray[3].flashedGasMolWeight, plusFracArray[3].wholeFluidMolWeight],
                        ['', plusFracArray[3].flashedLiquidSpecificGravity, plusFracArray[3].flashedGasSpecificGravity, plusFracArray[3].wholeFluidSpecificGravity]
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: this.commonStylesWithoutBorder as Partial<Styles>,
                    headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                    bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 }
                    },
                    didParseCell: function (data) {
                        // âœ… Apply bold only to header cell C36+
                        if (data.section === reportConstants.head && data.row.index === 0 && data.column.index === 0) {
                            data.cell.styles.font = reportConstants.eMprintCondensedBold;
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section } = data;
                        const lw = reportConstants.tableLinewidth;
                        const snap = (v: number) => Math.round(v) + lw / 2;
                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right

                        }
                        else if (section === reportConstants.body && row.index === 7) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);
                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            // âœ… LEFT border
                            doc.line(x, y, x, y + h);
                            // âœ… RIGHT border
                            doc.line(x + w, y, x + w, y + h);
                            // âœ… BOTTOM border (draw ONCE per row)
                            doc.line(x, y + h, x + w, y + h);
                            if (section === 'head') {
                                doc.line(x, y, x + w, y);
                            }
                            if (section === 'body' && row.index === 0 && column.index === 0) {
                                doc.line(x, y, x + w, y);
                            }
                        }
                        if (data.column.index === 0 && data.row.index === 2) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.specificGravityPDF;
                            const subscript = reportConstants.specificGravitySubscript;
                            const unit = '';

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.text(reportConstants.flashedSampleProperties, marginSize, tableStartY);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                tableStartY += 5;
                const oilAPIGravity = flashedSampleProperties.measuredDensityFlashedLiquid ?? 0;
                const oilAPIGravityComp = ((reportConstants.oilFlashValue1 / (oilAPIGravity / reportConstants.oilFlashValue2)) - reportConstants.oilFlashValue3).toFixed(1)
                autoTable(doc, {
                    head: [['', reportConstants.flashedLiquid, reportConstants.flashedGas, reportConstants.wholeFluid]],
                    body: [
                        [reportConstants.calculatedAvgMW, flashedSampleProperties.calculatedAvgMWFlashedLiquid, flashedSampleProperties.calculatedAvgMWFlashedGas, flashedSampleProperties.calculatedAvgMWWholeFluid],
                        [reportConstants.measuredAvgMW, flashedSampleProperties.measuredAvgMWFlashedLiquid, '', ''],
                        ['', flashedSampleProperties.calculatedDensityFlashedLiquid, '', ''],
                        [reportConstants.measuredDensity, flashedSampleProperties.measuredDensityFlashedLiquid, '', ''],
                        [reportConstants.moleFraction, flashedSampleProperties.moleFractionFlashedLiquid, flashedSampleProperties.moleFractionFlashedGas, flashedSampleProperties.moleFractionWholeFluid],
                        [reportConstants.massFraction, flashedSampleProperties.massFractionFlashedLiquid, flashedSampleProperties.massFractionFlashedGas, flashedSampleProperties.massFractionWholeFluid],
                        ['', '', flashedSampleProperties.gasGravityFlashedGas, ''],
                        [reportConstants.oilAPIGravity, oilAPIGravityComp],
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: {
                        fontSize: 8,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        lineColor: [0, 0, 0],
                        lineWidth: 0
                    },
                    headStyles: {
                        fontSize: 8,
                        halign: reportConstants.center,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        fontStyle: reportConstants.normal,
                        font: reportConstants.eMprintCondensedRegular,
                        cellPadding: { top: 0.5, bottom: 0.5 },
                        lineWidth: 0 // Remove border
                    },
                    bodyStyles: {
                        fontSize: 8,
                        halign: reportConstants.center,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        font: reportConstants.eMprintCondensedRegular,
                        cellPadding: { top: 0.5, bottom: 0.5 },
                        lineWidth: 0
                    },
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 }
                    },
                    didParseCell: function (data) {
                        // âœ… Apply bold only to header cell C36+
                        if (data.section === reportConstants.head) {
                            data.cell.styles.font = reportConstants.eMprintCondensedBold;
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section } = data;
                        const lw = reportConstants.tableLinewidth;
                        const snap = (v: number) => Math.round(v) + lw / 2;
                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right

                        }
                        else if (section === reportConstants.body && row.index === 7) {
                            const x = snap(cell.x);
                            const y = snap(cell.y);
                            const w = Math.round(cell.width);
                            const h = Math.round(cell.height);
                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            // âœ… LEFT border
                            doc.line(x, y, x, y + h);
                            // âœ… RIGHT border
                            doc.line(x + w, y, x + w, y + h);
                            // âœ… BOTTOM border (draw ONCE per row)
                            doc.line(x, y + h, x + w, y + h);
                            if (section === 'head') {
                                doc.line(x, y, x + w, y);
                            }
                            if (section === 'body' && row.index === 0 && column.index === 0) {
                                doc.line(x, y, x + w, y);
                            }
                        }

                        if (data.column.index === 0 && data.row.index === 2) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.calculatedDensityPDF;
                            const subscript = reportConstants.specificGravitySubscript;
                            const unit = reportConstants.calculatedDensityUnit;

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                        if (data.column.index === 0 && data.row.index === 6) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.gasGravityPDF;
                            const subscript = reportConstants.gasGravitySubscript;
                            const unit = '';

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    }
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.setFontSize(10);
                doc.text(reportConstants.volumetricFlash, marginSize, tableStartY);
                tableStartY += 5;
                doc.setFontSize(8);
                doc.text(`${reportConstants.flashConditionsExtra}: ${volumetricFlashPressure} ${reportConstants.pisa80}`, marginSize + 5, tableStartY);
                tableStartY += 3;

                autoTable(doc, {
                    head: [],
                    body: [
                        ['', volumetricFlashResult?.gorscfstb ?? ''],
                        ['', volumetricFlashResult?.formVolFactor ?? ''],
                        [reportConstants?.measuredDensityWithoutF ?? '', volumetricFlashResult?.measuredDensity ?? ''],
                        [reportConstants.oilAPIGravity, volumetricFlashResult?.oilAPIGravity ?? '']
                    ],
                    startY: Math.round(tableStartY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize, right: marginSize },
                    styles: this.commonStyles as Partial<Styles>,
                    headStyles: this.commonHeadStyles as Partial<Styles>,
                    bodyStyles: {
                        fontSize: 8,
                        halign: reportConstants.center,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        font: reportConstants.eMprintCondensedRegular,
                        cellPadding: { top: 0.5, bottom: 0.5 },
                        lineWidth: 0
                    },
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, column, doc, section } = data;

                        // Remove border for "ComponentName" cell (top-left header cell)
                        if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y, x + w, y);         // top
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        } else if (section === reportConstants.body && row.index === 3) {
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;

                            // Overdraw borders with white lines to "remove" them
                            doc.setDrawColor(255, 255, 255); // White
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.line(x, y + h, x + w, y + h); // bottom
                            doc.line(x, y, x, y + h);         // left
                            doc.line(x + w, y, x + w, y + h); // right
                        }
                        else {
                            // Draw the border for other cells
                            doc.setDrawColor(0, 0, 0); // Black
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.rect(cell.x, cell.y, cell.width, cell.height);
                        }
                        if (data.column.index === 0 && data.row.index === 0) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.gor;
                            const subscript = reportConstants.gorThree;
                            const unit = reportConstants.gorUnit;

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                        if (data.column.index === 0 && data.row.index === 1) {
                            const fontSize = 8;
                            const subFontSize = 6;

                            const baseText = reportConstants.formationVolFactorPDF;
                            const subscript = reportConstants.formationVolFactorSubscript;
                            const unit = reportConstants.formationVolFactorUnit;

                            doc.setFontSize(fontSize);
                            const baseTextWidth = doc.getTextWidth(baseText);

                            doc.setFontSize(subFontSize);
                            const subscriptWidth = doc.getTextWidth(subscript);

                            doc.setFontSize(fontSize);
                            const unitWidth = doc.getTextWidth(unit);

                            const totalTextWidth = baseTextWidth + subscriptWidth + unitWidth + 4; // spacing buffer
                            const baseX = data.cell.x + (data.cell.width - totalTextWidth) / 2;

                            const textHeight = fontSize * 0.3528;
                            const baseY = data.cell.y + (data.cell.height + textHeight) / 2 - 0.5;

                            doc.setFontSize(fontSize);
                            doc.text(baseText, baseX, baseY);

                            doc.setFontSize(subFontSize);
                            doc.text(subscript, baseX + baseTextWidth + 1, baseY - 1);

                            doc.setFontSize(fontSize);
                            doc.text(unit, baseX + baseTextWidth + subscriptWidth + 2, baseY);
                        }
                    },
                    tableWidth: doc.internal.pageSize.getWidth() * 0.3
                });
                tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

                doc.setFontSize(8);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.specificGravity, marginSize, tableStartY);
                tableStartY += 5;
                doc.text(reportConstants.gasGravityAir, marginSize, tableStartY);
                tableStartY += 5;
                doc.text(reportConstants.gorMeasurementCorrected, marginSize, tableStartY);
                tableStartY += 5;
                doc.text(reportConstants.formationVolumeFactor, marginSize, tableStartY);

                doc.addPage();

                currentY = 25;
                doc.setFontSize(10);
                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.text(wellName + ' ' + sampleName + ' ' + reportConstants.compositionalAnalysis, marginSize - 20, currentY);
                currentY += 5;

                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.materialBalanceCheckAndHoffmanPlot, marginSize - 20, currentY);
                currentY += 5;
                doc.setFontSize(10);
                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;
                const imageHeight = imageWidth * 0.8; // Adjust as needed

                const xPos = (pageWidth - imageWidth) / 2; // Center horizontally
                let yPos = currentY; // Starting Y position
                // First image

                const pdfW = 150;
                const pdfH = 115;

                // First image
                doc.addImage(
                    separatorData.graphImages[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

                // Update Y position for second image
                yPos += imageHeight - 10;
                // Second image

                doc.addImage(
                    separatorData.graphImages[1],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

            });
        }
    }

    private GetCellValue(cell: TableCell): string | number {
        if (typeof cell === 'object' && cell !== null && 'content' in cell) {
            return cell.content;
        }
        return cell ?? '';
    }

    // Applies a matching decimal numFmt to composition percentages, treating 0 as blank like the previous logic.
    private SetCompositionCellValue(cell: import('exceljs').Cell, rawCellData: TableCell): void {
        const unwrapped = this.GetCellValue(rawCellData);
        if (unwrapped !== '' && !isNaN(Number(unwrapped)) && Number(unwrapped) === 0) {
            cell.value = '';
            return;
        }
        setNumericOrTextCellValue(cell, unwrapped);
    }
}

